import { useEffect, useState, type ReactNode } from "react";
import type { EmailInboxView } from "../../../shared/types";
import { BrandTile } from "../../components/BrandIcon";
import { List, ListRow } from "../../components/List";
import { Switch } from "../../components/Switch";
import { Badge, ErrorText, PageHeader } from "../../components/ui";
import { get, put } from "../../lib/api";
import { Link, navigate } from "../../lib/router";

type SlackSettings = { enabled: boolean; hasBotToken: boolean; hasSigningSecret: boolean; team: string | null; botName: string | null };

/** The web chat's own mark: the app's panda, on the same tile as a channel's logo. */
function PandaTile() {
  return <img src="/favicon.png" alt="" className="size-10 shrink-0 rounded-xl bg-surface object-cover shadow-sm ring-1 ring-line" />;
}

/**
 * The top of a channel's own page: back to Channels, its logo and name, and its on/off on the name's row, which saves
 * on the spot. While it is off a line says what off means; everything under it stays editable.
 */
export function ChannelHeader({
  brand,
  name,
  subtitle,
  on,
  onToggle,
  toggleDisabled,
  toggleTitle,
  offText,
  actions,
}: {
  brand: string;
  name: string;
  subtitle: ReactNode;
  on: boolean;
  onToggle: (next: boolean) => void;
  toggleDisabled?: boolean;
  toggleTitle?: string;
  offText: string;
  actions?: ReactNode;
}) {
  return (
    <div className="px-4 pt-5 pb-4 md:px-6">
      <Link to="/admin/channels" className="text-sm text-muted hover:text-fg">← Channels</Link>
      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
        <BrandTile brand={brand} />
        <div className="min-w-0 flex-1">
          <h1 className="text-title font-semibold tracking-tight">{name}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted">{subtitle}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 self-start sm:self-center">
          {actions}
          <Switch label={on ? "On" : "Off"} checked={on} disabled={toggleDisabled} title={toggleTitle} onChange={onToggle} />
        </div>
      </div>
      {!on && <p className="mt-4 rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">{offText}</p>}
    </div>
  );
}

/** Every way people reach the agents besides each other: the web chat, Slack, email. Each opens its own settings. */
export function AdminChannels() {
  const [slack, setSlack] = useState<SlackSettings | null>(null);
  const [email, setEmail] = useState<EmailInboxView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<SlackSettings>("/api/slack/settings").then(setSlack, (e) => setError(e.message));
    get<EmailInboxView>("/api/email").then(setEmail, (e) => setError(e.message));
  }, []);

  const toggle = (fn: () => Promise<void>) => {
    setError(null);
    fn().catch((e) => setError((e as Error).message));
  };
  const slackReady = Boolean(slack?.hasBotToken && slack?.hasSigningSecret);
  // A switch in a clickable row toggles the channel and does not open it.
  const stay = (node: ReactNode) => <span onClick={(e) => e.stopPropagation()}>{node}</span>;

  return (
    <div className="pb-10">
      <PageHeader title="Channels" subtitle="Where people reach the agents. Switch one on or off here; open it for its settings." />
      <div className="space-y-4 px-4 md:px-6">
        <ErrorText error={error} />
        <List>
          <ListRow
            leading={<PandaTile />}
            title={<span className="font-medium">Web chat</span>}
            subtitle="Everyone signed in to AgentOS talks to the agents here. Nothing to set up."
            trailing={<Badge tone="emerald">always on</Badge>}
          />
          <ListRow
            leading={<BrandTile brand="slack" />}
            onClick={() => navigate("/admin/channels/slack")}
            title={
              <>
                <span className="font-medium">Slack</span>
                {slack && !slackReady && <Badge tone="amber">not set up</Badge>}
              </>
            }
            subtitle={slack ? (slack.team ? `Workspace ${slack.team}${slack.botName ? ` · @${slack.botName}` : ""}` : "DM the bot or @mention it in a channel.") : "Loading…"}
            trailing={
              slack &&
              stay(
                <Switch
                  aria-label="Slack on"
                  checked={slack.enabled}
                  disabled={!slackReady}
                  title={slackReady ? undefined : "Save the Slack credentials first"}
                  onChange={(enabled) => toggle(async () => setSlack(await put<SlackSettings>("/api/slack/settings", { enabled })))}
                />,
              )
            }
          />
          <ListRow
            leading={<BrandTile brand="gmail" />}
            onClick={() => navigate("/admin/channels/email")}
            title={
              <>
                <span className="font-medium">Email</span>
                {email?.problem && <Badge tone="amber">needs attention</Badge>}
              </>
            }
            subtitle={email ? `${email.settings.address}${email.connector ? ` · via ${email.connector}` : ""}` : "Loading…"}
            trailing={
              email &&
              stay(
                <Switch
                  aria-label="Email on"
                  checked={email.settings.enabled}
                  onChange={(enabled) => toggle(async () => setEmail(await put<EmailInboxView>("/api/email/settings", { enabled })))}
                />,
              )
            }
          />
        </List>
      </div>
    </div>
  );
}
